import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useAppState } from "../../contexts/App";
import { api } from "../../services/api";
import { isApiError } from "../../services/api/errors";

/**
 * Leaving the editor with changes not published yet (the Keepo's pages, its way in and out): the modal's copy, and
 * discarding them (the draft is deleted, back to My Keepos). A PLUS Keepo never published keeps its draft: it's the
 * whole Keepo, already paid for.
 */
export const useDiscardDraftExit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appState } = useAppState();
  const [removingDraft, setRemovingDraft] = useState(false);
  // The draft couldn't be discarded (not for want of one): says so and stays, instead of doing nothing
  const [discardFailed, setDiscardFailed] = useState(false);

  const exitMessages = useMemo(() => {
    const isNonPublishedPlus = !appState.published && appState.editingSiteSubscription?.type === "PLUS";
    const prefix = "pages.editor.sections.actions.exit.unsavedChanges.notRemove.modal";
    return {
      title: isNonPublishedPlus
        ? t(`${prefix}.title`)
        : t("pages.editor.sections.actions.exit.unsavedChanges.canRemove.modal.title"),
      confirmButton: isNonPublishedPlus ? t(`${prefix}.confirmButton`) : undefined,
      cancelButton: isNonPublishedPlus ? t(`${prefix}.cancelButton`) : undefined,
    };
  }, [appState, t]);

  const discardAndLeave = async () => {
    setRemovingDraft(true);
    setDiscardFailed(false);
    try {
      const isPlus = appState.editingSiteSubscription?.type === "PLUS";
      if (!isPlus || appState.published) {
        try {
          await api.deleteDraf(appState?.editingSiteId as string);
        } catch (e) {
          // A published Keepo with no draft left (it was published meanwhile) has nothing to discard: it's
          // already what's on the air. One never published still fails: that one would be lost
          if (!(appState.published && isApiError(e, "CANNOT_REMOVE_DRAFT"))) {
            throw e;
          }
        }
      }
      navigate("/sites", { replace: true });
    } catch (e) {
      setRemovingDraft(false);
      setDiscardFailed(true);
    }
  };

  return { exitMessages, discardAndLeave, removingDraft, discardFailed };
};
